import React, { useState, useMemo } from 'react';
import { Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MonthlyPackDialog = ({ open, onOpenChange, invoices }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [customerEmail, setCustomerEmail] = useState('');
  const [sending, setSending] = useState(false);

  const customers = useMemo(() => {
    const map = {};
    (invoices || []).forEach((inv) => {
      if (inv.is_monthly && inv.customer_email && (inv.invoice_date || '').slice(0, 7) === month) {
        map[inv.customer_email] = { name: inv.customer_name, email: inv.customer_email, number: inv.document_number };
      }
    });
    return Object.values(map).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [invoices, month]);

  const handleSend = async () => {
    if (!customerEmail) { toast.error('Please select a customer'); return; }
    const [year, m] = month.split('-').map(Number);
    setSending(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/invoices/monthly-pack`, {
        customer_email: customerEmail,
        year,
        month: m,
      });
      toast.success(`${res.data.message} (${res.data.invoice_number} + ${res.data.soa_number})`, { duration: 8000 });
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to send monthly pack', { duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="monthly-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail size={20} className="text-rose-700" /> Email Monthly Pack
          </DialogTitle>
          <DialogDescription>
            Emails the customer their monthly consolidated invoice AND Statement of Account in one email, with both PDFs attached. Generate both documents first.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>Month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setCustomerEmail(''); }}
              data-testid="monthly-pack-month-input"
            />
          </div>
          <div>
            <Label>Customer (with a monthly invoice in this month)</Label>
            <select
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="w-full h-10 border rounded-md px-3 bg-white text-sm"
              data-testid="monthly-pack-customer-select"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.email} value={c.email}>{c.name} — {c.number} ({c.email})</option>
              ))}
            </select>
            {customers.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">No monthly invoices (with customer email) found for this month. Use "Monthly Inv" to generate one first.</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSend}
            disabled={sending || !customerEmail}
            className="bg-rose-700 hover:bg-rose-800"
            data-testid="monthly-pack-send-btn"
          >
            {sending ? 'Sending...' : 'Send Email Pack'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
